'use client';

import React from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { Shield } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-8">
            <Shield className="w-6 h-6 text-amber-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">Privacy Policy</h1>
              <p className="text-slate-500 text-sm">Last updated: 1 April 2026</p>
            </div>
          </div>

          <div className="prose prose-invert prose-sm max-w-none space-y-8">
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">1. Who We Are</h2>
              <p className="text-slate-400 leading-relaxed text-sm">
                DrivenMarket Ltd ("DrivenMarket", "we", "us", or "our") operates the marketplace at drivenmark9994.builtwithrocket.new. We are registered in England and Wales. For data protection enquiries, contact us at <a href="mailto:privacy@drivenmarket.co.uk" className="text-amber-400 hover:text-amber-300">privacy@drivenmarket.co.uk</a>.
              </p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">2. Data We Collect</h2>
              <ul className="text-slate-400 text-sm space-y-2 list-disc list-inside leading-relaxed">
                <li><strong className="text-slate-300">Account data:</strong> name, email address, password (hashed), profile photo</li>
                <li><strong className="text-slate-300">Listing data:</strong> item descriptions, photos, prices, location</li>
                <li><strong className="text-slate-300">Transaction data:</strong> payment information processed via Stripe (we do not store card numbers)</li>
                <li><strong className="text-slate-300">Communications:</strong> messages sent between buyers and sellers on the platform</li>
                <li><strong className="text-slate-300">Usage data:</strong> pages visited, search queries, device type, IP address</li>
                <li><strong className="text-slate-300">Cookies:</strong> see our <Link href="/cookies" className="text-amber-400 hover:text-amber-300">Cookie Policy</Link></li>
              </ul>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">3. How We Use Your Data</h2>
              <ul className="text-slate-400 text-sm space-y-2 list-disc list-inside leading-relaxed">
                <li>To provide and operate the marketplace</li>
                <li>To process payments and prevent fraud</li>
                <li>To send transactional emails (order confirmations, messages, dispute updates)</li>
                <li>To comply with legal obligations including UK tax law</li>
                <li>To improve the platform through analytics</li>
              </ul>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">4. Legal Basis (UK GDPR)</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">We process your data under the following legal bases:</p>
              <ul className="text-slate-400 text-sm space-y-2 list-disc list-inside leading-relaxed">
                <li><strong className="text-slate-300">Contract:</strong> to fulfil our service to you as a buyer or seller</li>
                <li><strong className="text-slate-300">Legitimate interests:</strong> fraud prevention, platform security, analytics</li>
                <li><strong className="text-slate-300">Legal obligation:</strong> tax records, dispute resolution</li>
                <li><strong className="text-slate-300">Consent:</strong> marketing emails (you can withdraw at any time)</li>
              </ul>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">5. Data Sharing</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">We share data only with:</p>
              <ul className="text-slate-400 text-sm space-y-2 list-disc list-inside leading-relaxed">
                <li><strong className="text-slate-300">Stripe:</strong> payment processing (subject to Stripe's privacy policy)</li>
                <li><strong className="text-slate-300">Supabase:</strong> database and authentication hosting</li>
                <li><strong className="text-slate-300">Resend:</strong> transactional email delivery</li>
                <li>Law enforcement when legally required</li>
              </ul>
              <p className="text-slate-400 text-sm leading-relaxed mt-3">We do not sell your personal data to third parties.</p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">6. Your Rights</h2>
              <p className="text-slate-400 text-sm leading-relaxed mb-3">Under UK GDPR, you have the right to:</p>
              <ul className="text-slate-400 text-sm space-y-2 list-disc list-inside leading-relaxed">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion ("right to be forgotten")</li>
                <li>Object to processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-slate-400 text-sm leading-relaxed mt-3">To exercise any right, email <a href="mailto:privacy@drivenmarket.co.uk" className="text-amber-400 hover:text-amber-300">privacy@drivenmarket.co.uk</a>. You may also lodge a complaint with the <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:text-amber-300">ICO</a>.</p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">7. Data Retention</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                We retain account data for as long as your account is active. Transaction records are kept for 7 years to comply with UK tax law. You may request deletion of your account at any time; we will delete personal data within 30 days except where retention is required by law.
              </p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">8. Security</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                We use industry-standard security measures including TLS encryption, row-level security on our database, and regular security reviews. Passwords are hashed and never stored in plain text.
              </p>
            </div>

            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <h2 className="text-base font-semibold text-white mb-3">9. Contact</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                DrivenMarket Ltd, England &amp; Wales<br />
                Email: <a href="mailto:privacy@drivenmarket.co.uk" className="text-amber-400 hover:text-amber-300">privacy@drivenmarket.co.uk</a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
